import { ErrorMessage, FormikProps } from "formik";
import { useState } from "react";

type Option = { label: string; value: any };
export interface InputSelectProps {
  id: string;
  options: Option[];
  formikProps: FormikProps<any>;
  label?: string;
}

export const InputSelectField: React.FC<InputSelectProps> = ({
  id,
  label,
  options,
  formikProps,
}) => {
  const { errors, touched, values, setFieldValue } = formikProps;

  const selectedOption = options.find((o) => o.value === values[id]);
  const defaultValue = selectedOption ? selectedOption.label : "";

  const [inputValue, setInputValue] = useState(defaultValue);
  const [showDropdown, setShowDropdown] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState<number>(0);

  const filteredOptions = inputValue
    ? options.filter((o) =>
        o.label.toLowerCase().includes(inputValue.toLowerCase())
      )
    : options;

  const handleSelect = (option: { label: string; value: any }) => {
    setInputValue(option.label);
    setFieldValue(id, option.value);
    setFieldValue(id, option.value);
    setShowDropdown(false);
  };

  return (
    <div className="form-group">
      <label htmlFor={id}>{label}</label>
      <input
        id={id}
        list={`${id}-list`}
        autoComplete="off"
        className={
          "form-control" + (errors[id] && touched[id] ? " is-invalid" : "")
        }
        value={inputValue}
        onChange={(e) => {
          setInputValue(e.target.value);
          setShowDropdown(true);
          setHighlightIndex(0);
        }}
        onFocus={() => setShowDropdown(true)}
        onBlur={() => setShowDropdown(false)}
        onKeyDown={(e) => {
          if (!showDropdown || filteredOptions.length === 0) return;

          if (e.key === "ArrowDown") {
            e.preventDefault();
            setHighlightIndex((prev) =>
              prev < filteredOptions.length - 1 ? prev + 1 : prev
            );
          } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setHighlightIndex((prev) => (prev > 0 ? prev - 1 : 0));
          } else if (e.key === "Enter") {
            e.preventDefault();
            const selected = filteredOptions[highlightIndex];
            if (selected) {
              handleSelect(selected);
              setHighlightIndex(0);
            }
          }
        }}
      />
      <span
        className="position-absolute text-muted"
        style={{
          right: "15px",
          top: "50%",
          transform: "translateY(-20%)",
          pointerEvents: "none",
          fontSize: "1.5rem",
        }}
      >
        <span className="material-icons">keyboard_arrow_down</span>
      </span>

      <Dropdown
        items={filteredOptions}
        visible={showDropdown && filteredOptions.length > 0}
        highlightIndex={highlightIndex}
        onSelect={handleSelect}
      />

      <ErrorMessage name={id} component="div" className="invalid-feedback" />
    </div>
  );
};

type DropdownProps = {
  items: Option[];
  onSelect: (option: { label: string; value: any }) => void;
  visible: boolean;
  highlightIndex: number;
  style?: React.CSSProperties;
};

const Dropdown = ({
  items,
  onSelect,
  visible,
  highlightIndex,
  style,
}: DropdownProps) => {
  if (!visible || items.length === 0) return null;

  return (
    <div
      className="dropdown-menu show"
      style={{
        position: "absolute",
        top: "100%",
        left: 10,
        right: 0,
        maxHeight: "200px",
        overflowY: "auto",
        ...style,
      }}
    >
      {items.map((item, index) => (
        <button
          key={item.value}
          type="button"
          className={`dropdown-item ${
            index === highlightIndex ? "active" : ""
          }`}
          onMouseDown={() => onSelect(item)}
        >
          {item.label}
        </button>
      ))}
    </div>
  );
};
